import { Link } from 'react-router-dom';
import { AREE } from '../../constants/areas';
import { fmtNum } from '../../lib/periodFormat';
import { formulaForPeriodoTarget } from '../../lib/formula';
import Tooltip from '../nav/Tooltip';

/**
 * TargetNumericiSection — target numerici per il periodo
 * (suggerito / impegnato / reale). Mostra tooltip con la formula di derivazione
 * se applicabile.
 *
 * Layout responsive:
 * - ≥ md (768px+): tabella tradizionale a 4 colonne
 * - < md: card stack (1 card per KR con 3 mini-righe Sugg / Impeg / Reale)
 *
 * Props:
 *   - targets : array di { id, key_result_id, periodo, etichetta, ripartizione, suggerito, impegnato, reale }
 *   - krById  : mappa { krId: krOggetto }
 */
export default function TargetNumericiSection({ targets, krById }) {
  if (targets.length === 0) return null;
  return (
    <section className="mb-12" data-testid="section-target-numerici">
      <h2 className="font-display text-xl md:text-2xl tracking-tight mb-4">Target numerici del periodo</h2>

      {/* ── Desktop / tablet: TABLE ─────────────────────────────────────── */}
      <div className="hidden md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink">
              <th className="text-left py-2 font-mono text-xs uppercase tracking-[0.15em] text-soft">Key result</th>
              <th className="text-right py-2 font-mono text-xs uppercase tracking-[0.15em] text-soft">Suggerito</th>
              <th className="text-right py-2 font-mono text-xs uppercase tracking-[0.15em] text-soft">Impegnato</th>
              <th className="text-right py-2 font-mono text-xs uppercase tracking-[0.15em] text-soft">Reale</th>
            </tr>
          </thead>
          <tbody>
            {targets.map((t) => {
              const kr = krById[t.key_result_id];
              const meta = AREE[(kr.area || '').toLowerCase()] || { color: 'var(--qp-ink)' };
              const formula = formulaForPeriodoTarget({
                kr,
                periodo: t.periodo,
                etichetta: t.etichetta,
                ripartizione: t.ripartizione,
                suggerito: t.suggerito,
                impegnato: t.impegnato,
              });
              const sugCell = (
                <span className={'font-mono tabular-nums ' + (formula ? 'border-b border-dotted border-mute cursor-help' : '')}>
                  {fmtNum(t.suggerito, kr.unita)}
                </span>
              );
              const impCell = t.impegnato != null ? (
                <span className={'font-mono tabular-nums text-soft ' + (formula ? 'border-b border-dotted border-mute cursor-help' : '')}>
                  {fmtNum(t.impegnato, kr.unita)}
                </span>
              ) : <span className="font-mono tabular-nums text-soft">—</span>;
              return (
                <tr key={t.id} className="border-b border-linesoft" data-testid={`row-target-${t.id}`}>
                  <td className="py-3">
                    <Link to={`/obiettivo/${kr.obiettivo_id}`} className="hover:text-accent">
                      <span className="font-mono text-xs text-mute mr-2">{String(kr.obiettivo_ordine).padStart(2, '0')}</span>
                      <span style={{ color: meta.color }}>{kr.titolo}</span>
                    </Link>
                  </td>
                  <td className="py-3 text-right" data-testid={`target-sug-${t.id}`}>
                    {formula ? <Tooltip label={formula} side="left" wide maxWidth={300} delay={200}>{sugCell}</Tooltip> : sugCell}
                  </td>
                  <td className="py-3 text-right" data-testid={`target-imp-${t.id}`}>
                    {formula && t.impegnato != null
                      ? <Tooltip label={formula} side="left" wide maxWidth={300} delay={200}>{impCell}</Tooltip>
                      : impCell}
                  </td>
                  <td className="py-3 text-right font-mono tabular-nums">{fmtNum(t.reale, kr.unita)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Mobile (<768px): CARD STACK ─────────────────────────────────── */}
      <div className="md:hidden flex flex-col gap-3">
        {targets.map((t) => {
          const kr = krById[t.key_result_id];
          const meta = AREE[(kr.area || '').toLowerCase()] || { color: 'var(--qp-ink)' };
          const formula = formulaForPeriodoTarget({
            kr,
            periodo: t.periodo,
            etichetta: t.etichetta,
            ripartizione: t.ripartizione,
            suggerito: t.suggerito,
            impegnato: t.impegnato,
          });
          return (
            <div
              key={t.id}
              className="border border-linesoft p-3 bg-paper"
              data-testid={`card-target-${t.id}`}
            >
              <Link
                to={`/obiettivo/${kr.obiettivo_id}`}
                className="block mb-2 hover:text-accent"
              >
                <span className="font-mono text-xs text-mute mr-2">
                  {String(kr.obiettivo_ordine).padStart(2, '0')}
                </span>
                <span style={{ color: meta.color }} className="font-body text-sm leading-snug">
                  {kr.titolo}
                </span>
              </Link>
              <dl className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-mute mb-0.5">
                    Sugg.
                  </dt>
                  <dd
                    className="font-mono tabular-nums"
                    data-testid={`target-sug-${t.id}-m`}
                    title={formula || undefined}
                  >
                    {fmtNum(t.suggerito, kr.unita)}
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-mute mb-0.5">
                    Impeg.
                  </dt>
                  <dd
                    className="font-mono tabular-nums text-soft"
                    data-testid={`target-imp-${t.id}-m`}
                    title={formula || undefined}
                  >
                    {t.impegnato != null ? fmtNum(t.impegnato, kr.unita) : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-mute mb-0.5">
                    Reale
                  </dt>
                  <dd className="font-mono tabular-nums">
                    {fmtNum(t.reale, kr.unita)}
                  </dd>
                </div>
              </dl>
            </div>
          );
        })}
      </div>
    </section>
  );
}
